import { apiFetch } from "@/lib/apiClient";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

// Eventos que também vão pelo servidor (API de Conversões do Meta), com o mesmo id do evento
// do navegador pra o Meta contar uma vez só. Precisa bater com META_BROWSER_EVENTS no backend
// (utils/metaConversions.ts). O Subscribe fica de fora: quem envia é o servidor, na hora em
// que o pagamento é confirmado.
const SERVER_MIRRORED_EVENTS = new Set(["ViewContent", "Lead", "CompleteRegistration", "InitiateCheckout", "AddPaymentInfo"]);

function readCookie(name: string) {
  return document.cookie
    .split("; ")
    .find((c) => c.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

/**
 * Identificadores que o pixel do Meta grava no navegador: _fbp (o navegador) e _fbc (o clique
 * no anúncio). Na primeira visita vinda de um anúncio o pixel pode ainda não ter gravado o
 * _fbc, então ele é montado a partir do fbclid da URL, no mesmo formato.
 */
export function getMetaBrowserIds() {
  const fbclid = new URLSearchParams(window.location.search).get("fbclid");
  return {
    fbp: readCookie("_fbp"),
    fbc: readCookie("_fbc") ?? (fbclid ? `fb.1.${Date.now()}.${fbclid}` : undefined),
  };
}

function newEventId() {
  return typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Dispara um evento do Meta Pixel (o script base já vai no index.html) e, pros eventos
 * espelhados, manda o mesmo evento pro servidor repassar pela API de Conversões. Não quebra
 * nada se o `fbq` não existir (script ainda carregando ou bloqueador de anúncio) - nesse caso o
 * evento ainda chega pelo servidor.
 */
export function trackPixelEvent(event: string, params?: Record<string, unknown>, eventId: string = newEventId()) {
  if (typeof window === "undefined") return;

  if (typeof window.fbq === "function") {
    window.fbq("track", event, params ?? {}, { eventID: eventId });
  }

  if (SERVER_MIRRORED_EVENTS.has(event)) {
    apiFetch("/meta/events", {
      method: "POST",
      keepalive: true,
      skipAuthRetry: true,
      body: {
        eventName: event,
        eventId,
        eventSourceUrl: window.location.origin + window.location.pathname,
        ...getMetaBrowserIds(),
        value: typeof params?.value === "number" ? params.value : undefined,
      },
    }).catch(() => {
      // rastreamento nunca pode atrapalhar o uso do app
    });
  }
}

/**
 * Igual ao trackPixelEvent, mas no máximo uma vez por visita (aba). Pra eventos de "chegou em
 * tal tela" - o visitante da demonstração vai e volta do cadastro a cada coisa que tenta anotar,
 * e sem isso cada ida contaria como um Lead novo no Meta.
 */
export function trackPixelEventOncePerVisit(event: string) {
  const key = `controlenext-pixel-${event}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {
    // sessionStorage indisponível: melhor contar a mais do que deixar de contar
  }
  trackPixelEvent(event);
}
